import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Alert,
  Tabs,
  Tab,
} from 'react-bootstrap';

function buildUserPayload(formData) {
  const payload = {
    username: formData.username,
    email: formData.email,
    first_name: formData.first_name || null,
    last_name: formData.last_name || null,
    role: formData.role,
    region: formData.region || 'EMEA',
    telephone: formData.telephone || null,
    mobile: formData.mobile || null,
    company_name: formData.company_name || null,
    address_line1: formData.address_line1 || null,
    address_line2: formData.address_line2 || null,
    town: formData.town || null,
    county: formData.county || null,
    postcode: formData.postcode || null,
    country: formData.country || null,
    active: formData.active,
    blueprint: formData.blueprint,
    assigned_admin_id: formData.assigned_admin_id || null,
    email_notifications_enabled: formData.email_notifications_enabled,
  };

  if (formData.password) {
    payload.password = formData.password;
  }

  return payload;
}

const EMPTY_USER_FORM = {
  username: '',
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  role: 'customer',
  region: 'EMEA',
  telephone: '',
  mobile: '',
  company_name: '',
  address_line1: '',
  address_line2: '',
  town: '',
  county: '',
  postcode: '',
  country: '',
  active: true,
  blueprint: false,
  assigned_admin_id: null,
  email_notifications_enabled: true,
};

function UserModal({ user, onClose, onSave, error }) {
  const [formData, setFormData] = useState(EMPTY_USER_FORM);

  const [adminUsers, setAdminUsers] = useState([]);

  useEffect(() => {
    if (user) {
      setFormData({
        ...user,
        region: user.region || 'EMEA',
        password: '', // Don't show existing password
      });
    } else {
      setFormData({ ...EMPTY_USER_FORM });
    }
    fetchAdminUsers();
  }, [user]);

  const fetchAdminUsers = async () => {
    try {
      const response = await axios.get('/api/user-management/admins');
      setAdminUsers(response.data);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let nextValue = type === 'checkbox' ? checked : value;
    if (name === 'assigned_admin_id') {
      nextValue = value === '' ? null : Number(value);
    }
    setFormData({
      ...formData,
      [name]: nextValue,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(buildUserPayload(formData));
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" style={{ zIndex: 1060 }}>
      <Form onSubmit={handleSubmit} autoComplete="off">
        <Modal.Header closeButton>
          <Modal.Title>{user ? 'Edit User' : 'Add New User'}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => {}}>
              {error}
            </Alert>
          )}
          <Tabs defaultActiveKey="account" className="mb-3">
            <Tab eventKey="account" title="Account">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Username <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      autoComplete="off"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Password {!user && <span className="text-danger">*</span>}
                      {user && <small className="text-muted"> (leave blank to keep current)</small>}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      autoComplete="new-password"
                      required={!user}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Role <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select name="role" value={formData.role} onChange={handleChange} required>
                      <option value="customer">Customer User</option>
                      <option value="admin">Admin User</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Region</Form.Label>
                    <Form.Select name="region" value={formData.region} onChange={handleChange}>
                      <option value="EMEA">EMEA</option>
                      <option value="APAC">APAC</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="profile" title="Profile">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Company Name</Form.Label>
                <Form.Control
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telephone</Form.Label>
                    <Form.Control
                      type="text"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      maxLength="60"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mobile</Form.Label>
                    <Form.Control
                      type="text"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      maxLength="60"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="address" title="Address &amp; Settings">
              <Form.Group className="mb-3">
                <Form.Label>Address Line 1</Form.Label>
                <Form.Control
                  type="text"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Address Line 2</Form.Label>
                <Form.Control
                  type="text"
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleChange}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Town</Form.Label>
                    <Form.Control
                      type="text"
                      name="town"
                      value={formData.town}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>County</Form.Label>
                    <Form.Control
                      type="text"
                      name="county"
                      value={formData.county}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postcode</Form.Label>
                    <Form.Control
                      type="text"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleChange}
                      maxLength="8"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <Form.Control
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      name="active"
                      label="Active"
                      checked={formData.active}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      name="blueprint"
                      label="Blueprint User"
                      checked={formData.blueprint}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assigned Admin</Form.Label>
                    <Form.Select
                      name="assigned_admin_id"
                      value={formData.assigned_admin_id || ''}
                      onChange={handleChange}
                    >
                      <option value="">-- No Admin Assigned --</option>
                      {adminUsers.map((admin) => (
                        <option key={admin.id} value={admin.id}>
                          {admin.first_name && admin.last_name
                            ? `${admin.first_name} ${admin.last_name} (${admin.username})`
                            : admin.username}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Assign an admin user to track this customer's activity
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      name="email_notifications_enabled"
                      label="Enable Email Notifications"
                      checked={formData.email_notifications_enabled}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted d-block">
                      Send email updates to assigned admin when this user makes changes
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {user ? 'Update User' : 'Create User'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function partitionUsers(users) {
  const blueprintUsers = users.filter((user) => user.blueprint);
  const adminUsers = users.filter((user) => user.role === 'admin' && !user.blueprint);
  const customerUsers = users.filter((user) => user.role !== 'admin' && !user.blueprint);

  return { customerUsers, adminUsers, blueprintUsers };
}

function UserTable({ users, onEdit, onDelete, onReopen }) {
  if (users.length === 0) {
    return <div className="text-center p-3 text-muted">No users in this section.</div>;
  }

  return (
    <Table striped bordered hover responsive className="mb-0">
      <thead>
        <tr>
          <th>Username</th>
          <th>Name</th>
          <th>Email</th>
          <th>Company</th>
          <th>Role</th>
          <th>Status</th>
          <th>Data Complete</th>
          <th>Created</th>
          <th style={{ width: '210px' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {users.map((user) => (
          <tr key={user.id}>
            <td>
              <strong>{user.username}</strong>
            </td>
            <td>{[user.first_name, user.last_name].filter(Boolean).join(' ') || '-'}</td>
            <td>{user.email}</td>
            <td>{user.company_name || '-'}</td>
            <td>
              <Badge bg={user.role === 'admin' ? 'danger' : 'info'}>
                {user.role === 'admin' ? 'Admin' : 'Customer'}
              </Badge>
            </td>
            <td>
              <Badge bg={user.active ? 'success' : 'secondary'}>
                {user.active ? 'Active' : 'Inactive'}
              </Badge>
            </td>
            <td>
              {user.data_complete_at
                ? new Date(user.data_complete_at).toLocaleDateString('en-GB')
                : '-'}
            </td>
            <td>{new Date(user.created_at).toLocaleDateString()}</td>
            <td>
              <div className="d-flex gap-1">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => onEdit(user)}
                  title="Edit user"
                >
                  <i className="fas fa-edit"></i>
                </Button>
                {user.data_complete_at && user.role !== 'admin' && (
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={() => onReopen(user)}
                    title="Allow editing again"
                  >
                    <i className="fas fa-unlock"></i>
                  </Button>
                )}
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onDelete(user)}
                  title="Delete user"
                >
                  <i className="fas fa-trash"></i>
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function UserSection({ title, description, users, onEdit, onDelete, onReopen }) {
  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-0 fw-semibold">{title}</h5>
          {description && <small className="text-muted">{description}</small>}
        </div>
        <Badge bg="secondary">{users.length}</Badge>
      </Card.Header>
      <Card.Body className="p-0">
        <UserTable users={users} onEdit={onEdit} onDelete={onDelete} onReopen={onReopen} />
      </Card.Body>
    </Card>
  );
}

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/user-management');
      setUsers(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to access this page. Admin access required.');
      } else {
        setError('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setModalError('');
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setModalError('');
    setShowModal(true);
  };

  const handleSaveUser = async (userData) => {
    try {
      if (editingUser) {
        await axios.put(`/api/user-management/${editingUser.id}`, userData);
        setSuccess('User updated successfully');
      } else {
        await axios.post('/api/user-management', userData);
        setSuccess('User created successfully');
      }
      setShowModal(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving user:', error);
      setModalError(error.response?.data?.error || 'Failed to save user');
    }
  };

  const handleReopenUserData = async (user) => {
    if (
      !window.confirm(
        `Allow "${user.username}" to edit their data again? This will clear their data complete confirmation and re-enable editing for their account.`
      )
    ) {
      return;
    }

    try {
      await axios.post(`/api/user-management/${user.id}/reopen-data`);
      setSuccess(`"${user.username}" can edit data again`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error reopening user data:', error);
      setError(error.response?.data?.error || 'Failed to reopen user data');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteUser = async (user) => {
    if (
      window.confirm(
        `Are you sure you want to delete user "${user.username}"? This action cannot be undone.`
      )
    ) {
      try {
        await axios.delete(`/api/user-management/${user.id}`);
        setSuccess('User deleted successfully');
        fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        console.error('Error deleting user:', error);
        setError(error.response?.data?.error || 'Failed to delete user');
        setTimeout(() => setError(''), 5000);
      }
    }
  };

  const { customerUsers, adminUsers, blueprintUsers } = useMemo(
    () => partitionUsers(users),
    [users]
  );

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i className="fas fa-users-cog me-3 text-primary" style={{ fontSize: '2.5rem' }}></i>
            <div>
              <h1 className="mb-0">User Management</h1>
              <p className="text-muted mb-0">Manage customer, admin, and blueprint users</p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button variant="primary" onClick={handleAddUser}>
            <i className="fas fa-user-plus me-2"></i>
            Add User
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          {loading ? (
            <Card>
              <Card.Body>
                <div className="text-center p-3">Loading users...</div>
              </Card.Body>
            </Card>
          ) : users.length === 0 ? (
            <Card>
              <Card.Body>
                <div className="text-center p-3">No users found.</div>
              </Card.Body>
            </Card>
          ) : (
            <>
              <UserSection
                title="Customer"
                description="Standard customer portal accounts"
                users={customerUsers}
                onEdit={handleEditUser}
                onDelete={handleDeleteUser}
                onReopen={handleReopenUserData}
              />
              <UserSection
                title="Admin"
                description="Administrative accounts with full access"
                users={adminUsers}
                onEdit={handleEditUser}
                onDelete={handleDeleteUser}
                onReopen={handleReopenUserData}
              />
              <UserSection
                title="Blueprint"
                description="Template accounts used for fleet wizard and reference data"
                users={blueprintUsers}
                onEdit={handleEditUser}
                onDelete={handleDeleteUser}
                onReopen={handleReopenUserData}
              />
            </>
          )}
        </Col>
      </Row>

      {showModal && (
        <UserModal
          key={editingUser?.id ?? 'new'}
          user={editingUser}
          onClose={() => {
            setShowModal(false);
            setEditingUser(null);
          }}
          onSave={handleSaveUser}
          error={modalError}
        />
      )}
    </Container>
  );
}

export default UserManagement;
